import { useEffect, useState, type MouseEvent } from 'react'
import AnchoredPopover from '../../../components/AnchoredPopover'
import Sheet from '../../../components/Sheet'
import ModelIcon from '../../../components/ModelIcon'
import RestrictionShields from '../../../components/RestrictionShields'
import { getModelHelp, type ModelHelpData } from '../../../api/modelHelp'
import type { ModelCategory } from '../../../data/models'
import { characterFitLine, type CharacterFit } from '../../../utils/characterFit'
import type { PromptRefsHelp } from '../../../utils/promptRefsHelp'

/**
 * ModelHelpSheet — «?»-справка текущей модели: CTA интерактивного обучения
 * (SpotlightTour) + «Особенности модели» + «Частые вопросы».
 *
 * Контент ведётся в админке («Инструкции нейросетей»), грузится лениво с кэшем
 * и тройным фолбэком (модель → категория → хардкод) — модал никогда не пустой.
 * Десктоп — AnchoredPopover у кнопки «?», мобила — bottom-sheet.
 */
interface Props {
	open: boolean
	onClose: () => void
	anchorEl: HTMLElement | null
	adapter: string
	modelId: string
	modelName: string
	modelIcon?: string
	category: ModelCategory
	/** Бейдж режима в шапке: «Text to Image», «Music», «Чат»… */
	modeLabel: string
	/** Если задан — показываем CTA «Запустить интерактивное обучение». */
	onStartTour?: () => void
	/** Направление десктоп-поповера: 'above' для кнопок у низа экрана (чат). */
	preferPlacement?: 'above' | 'below'
	/** Для чего пригодна модель (бэк выводит из её возможностей). */
	scenarios?: { key: string; label: string; detail: string }[]
	/** «Как встанет персонаж» — вердикт резолвера бэка (character_fit). */
	characterFit?: CharacterFit | null
	/** «Как ссылаться на файлы» — пилюли и токены модели из общего определения (utils/promptRefsHelp). */
	promptRefs?: PromptRefsHelp | null
}

const DOT_COLORS = ['#7c6cf0', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899']

const CHEVRON = (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M6 9l6 6 6-6" />
	</svg>
)

export default function ModelHelpSheet({
	open,
	onClose,
	anchorEl,
	adapter,
	modelId,
	modelName,
	modelIcon,
	category,
	modeLabel,
	onStartTour,
	preferPlacement,
	scenarios,
	characterFit,
	promptRefs,
}: Props) {
	// Строка про персонажей — всем: раздел открыт, прятать пояснение не от кого.
	const charLine = characterFitLine(characterFit)
	const [isMobile, setIsMobile] = useState(
		() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
	)
	useEffect(() => {
		const mq = window.matchMedia('(max-width: 640px)')
		const fn = (e: MediaQueryListEvent) => setIsMobile(e.matches)
		mq.addEventListener('change', fn)
		return () => mq.removeEventListener('change', fn)
	}, [])

	const [data, setData] = useState<ModelHelpData | null>(null)
	// Эксклюзивный аккордеон: открыт максимум один пункт — открытие нового
	// плавно закрывает предыдущий (grid-анимация отрабатывает обе стороны).
	const [openKey, setOpenKey] = useState<string | null>(null)
	// Derived-loading без setState в теле эффекта (react-hooks/set-state-in-effect):
	// пока loadedKey не догнал текущий ключ модели — показываем скелетон.
	const helpKey = `${adapter}::${modelId}::${category}`
	const [loadedKey, setLoadedKey] = useState<string | null>(null)
	const loading = loadedKey !== helpKey

	useEffect(() => {
		if (!open || loadedKey === helpKey) return
		let alive = true
		getModelHelp(adapter, modelId, category).then((d) => {
			if (!alive) return
			setData(d)
			// Первый пункт раскрыт по умолчанию — как в макете.
			setOpenKey(d.features.length > 0 ? 'f0' : d.faq.length > 0 ? 'q0' : null)
			setLoadedKey(helpKey)
		})
		return () => { alive = false }
	}, [open, helpKey, loadedKey, adapter, modelId, category])

	const toggle = (k: string) => setOpenKey((prev) => (prev === k ? null : k))

	const renderItem = (key: string, marker: React.ReactNode, title: string, body: string) => {
		const isOpen = openKey === key
		return (
			<div key={key} className={`model-help__item${isOpen ? ' is-open' : ''}`}>
				<button type="button" className="model-help__item-head" onClick={() => toggle(key)} aria-expanded={isOpen}>
					{marker}
					<span className="model-help__item-title">{title}</span>
					<span className="model-help__chev" aria-hidden="true">{CHEVRON}</span>
				</button>
				{/* Тело рендерится всегда — grid-анимация 0fr↔1fr раскрывает/схлопывает
				    плавно в обе стороны. Отдельный clip-слой БЕЗ паддингов обязателен:
				    паддинг на самом grid-элементе не даёт треку схлопнуться до нуля. */}
				{body && (
					<div className="model-help__item-grow">
						<div className="model-help__item-clip">
							<div className="model-help__item-body">{body}</div>
						</div>
					</div>
				)}
			</div>
		)
	}

	const content = (
		<div className="model-help">
			<div className="model-help__head">
				<span className="model-help__icon">
					{/* ModelIcon — тот же механизм, что в селекте моделей: чёрные
					    monochrome-иконки (DARK_ICONS) в тёмной теме инвертируются. */}
					{modelIcon ? <ModelIcon icon={modelIcon} name={modelName} size={22} /> : <span aria-hidden="true">?</span>}
				</span>
				<div className="model-help__title-wrap">
					<div className="model-help__name">{modelName}</div>
					<div className="model-help__sub">Обучение · особенности · FAQ</div>
				</div>
				<span className="model-help__mode">{modeLabel}</span>
				{!isMobile && (
					<button type="button" className="model-help__close" onClick={onClose} aria-label="Закрыть">
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
							<path d="M18 6 6 18M6 6l12 12" />
						</svg>
					</button>
				)}
			</div>

			<div className="model-help__body">
				{onStartTour && (
					<button
						type="button"
						className="model-help__tour"
						onClick={(e: MouseEvent) => { e.stopPropagation(); onStartTour() }}
					>
						<span className="model-help__tour-icon" aria-hidden="true">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
								<circle cx="12" cy="12" r="3" />
								<path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
							</svg>
						</span>
						<span className="model-help__tour-text">
							<span className="model-help__tour-title">Запустить интерактивное обучение</span>
							<span className="model-help__tour-sub">Подсветим каждый элемент виджета по шагам</span>
						</span>
						<span className="model-help__tour-arrow" aria-hidden="true">→</span>
					</button>
				)}

				{/* «Что умеет» — не из админского контента, а выведено бэком из
				    реальных возможностей модели. Поэтому рендерим сразу, не
				    дожидаясь загрузки справки: данные уже в самой модели, и
				    даже у нейронки, которой никто не писал инструкцию, экран
				    сообщает правду о том, что она делает с фото. */}
				{scenarios && scenarios.length > 0 && (
					<>
						<div className="model-help__label model-help__label--caps">
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="M13 2 4.5 13H11l-1 9 8.5-11H12l1-9Z" />
							</svg>
							Что умеет эта модель
						</div>
						<div className="model-help__caps">
							{scenarios.map((s) => (
								<div key={s.key} className="model-help__cap">
									<span className="model-help__cap-chip">{s.label}</span>
									<span className="model-help__cap-text">{s.detail}</span>
								</div>
							))}
						</div>
					</>
				)}

				{/* «Как ссылаться на файлы» — из общего определения переменных: чем понятнее
				    пилюли и роли файлов, тем лучше результат (владелец 25.09). */}
				{promptRefs && promptRefs.items.length > 0 && (
					<>
						<div className="model-help__label model-help__label--caps">
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
								<path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
							</svg>
							Как ссылаться на файлы
						</div>
						<div className="model-help__caps">
							{promptRefs.items.map((it) => (
								<div key={it.chip} className="model-help__cap">
									<span className="model-help__cap-chip">{it.chip}</span>
									<span className="model-help__cap-text">{it.text}</span>
								</div>
							))}
							<div className="model-help__cap">
								<span className="model-help__cap-chip">Пример</span>
								<span className="model-help__cap-text">{promptRefs.example.replace(/^Пример: /, '')} {promptRefs.note}</span>
							</div>
						</div>
					</>
				)}

				{charLine && (
					<div className={`model-help__char${charLine.warn ? ' model-help__char--warn' : ''}`}>
						<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
							<circle cx="12" cy="8" r="3.4" />
							<path d="M5 20c1.3-3.4 3.9-5 7-5s5.7 1.6 7 5" />
						</svg>
						<span><b>Аватар:</b> {charLine.text}</span>
					</div>
				)}

				{loading && (
					<div className="model-help__skeleton" aria-hidden="true">
						<span /><span /><span />
					</div>
				)}

				{/* Ограничения контента: щиты с заливкой по жёсткости + текст.
				    Стоят ПЕРЕД особенностями — «чего модель не сделает» юзер
				    должен увидеть до того, как вложится в промпт. */}
				{!loading && data && (data.restrictions?.length || 0) > 0 && (
					<>
						<div className="model-help__label model-help__label--limits">
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
								<path d="M12 2.6 19.6 5.7V11c0 5.2-3.1 9.1-7.6 10.6C7.5 20.1 4.4 16.2 4.4 11V5.7Z" />
							</svg>
							Ограничения контента
						</div>
						{data.restrictions.map((r, i) => (
							<div key={`r${i}`} className="model-help__limit">
								<RestrictionShields items={[r]} size={15} />
								<div className="model-help__limit-body">
									<div className="model-help__limit-title">{r.title}</div>
									{r.text && <div className="model-help__limit-text">{r.text}</div>}
								</div>
							</div>
						))}
					</>
				)}

				{!loading && data && data.features.length > 0 && (
					<>
						<div className="model-help__label">
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
								<path d="M9 18h6M10 21h4M12 3a6 6 0 0 1 3.6 10.8c-.5.4-.6 1-.6 1.7V16h-6v-.5c0-.7-.1-1.3-.6-1.7A6 6 0 0 1 12 3Z" />
							</svg>
							Особенности модели
						</div>
						{data.features.map((f, i) =>
							renderItem(
								`f${i}`,
								<span className="model-help__dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} aria-hidden="true" />,
								f.title,
								f.body,
							),
						)}
					</>
				)}

				{!loading && data && data.faq.length > 0 && (
					<>
						<div className="model-help__label model-help__label--faq">
							<span className="model-help__label-q" aria-hidden="true">?</span>
							Частые вопросы
						</div>
						{data.faq.map((f, i) =>
							renderItem(
								`q${i}`,
								<span className="model-help__q" aria-hidden="true">?</span>,
								f.q,
								f.a,
							),
						)}
					</>
				)}
			</div>
		</div>
	)

	if (isMobile) {
		return (
			<Sheet open={open} onClose={onClose} variant="bottom" showHandle contentClassName="model-help-sheet">
				{content}
			</Sheet>
		)
	}
	return (
		<AnchoredPopover open={open} onClose={onClose} anchorEl={anchorEl} width={380} align="center" className="model-help-popover" preferDirection={preferPlacement}>
			{content}
		</AnchoredPopover>
	)
}
